import { useContext, useEffect, useMemo, useState } from 'react';

import { BacktestingContext } from '../../../../providers/backtesting/backtesting-provider';
import { buildBacktestReport } from '../../../../providers/backtesting/adapters/build-backtest-report';
import { IPortfolioPerformanceAccountReport } from '../../../../providers/portfolio-performance/portfolio-performance.types';
import type {
  BacktestResultTabKey,
  StrategyFormData,
} from '../../../../providers/backtesting/types';

import './styles.scss';
import { BacktestEmptyStudy } from './backtest-empty-study';
import { BacktestOverviewTable } from './overview/backtest-overview-table';
import { BacktestRunPrompt } from './backtest-run-prompt';
import { MarketData } from './market-data';
import { MarketRisk } from './market-risk';
import { Overview } from './overview';
import { TransactionLog } from './transaction-log';
import { Summary } from './summary';
import { Specification } from './specification';
import { RequestView } from './request';

// Mirrors the live Portfolio Performance page shape (IM-1840): one persistent
// overview table listing every stratlet, and below it a single tab bar scoped
// to whichever stratlet is open in that table. The tabs mirror the PP account
// card's own bar (Performance / Market Risk / ... ), not a page-level view
// swap. "Overview" is kept as the tab key for backwards compatibility with
// persisted activeResultTab state; its label is "Performance".
const RESULT_TABS: Array<{ key: BacktestResultTabKey; label: string }> = [
  { key: 'overview', label: 'Performance' },
  { key: 'market-risk', label: 'Market Risk' },
  { key: 'market-data', label: 'Market Data' },
  { key: 'transaction-log', label: 'Transaction Log' },
  { key: 'summary', label: 'Summary' },
  { key: 'specification', label: 'Specification' },
  // Debug/inspection tab — always mountable, shows the raw request last sent.
  { key: 'request', label: 'Request' },
] as const;

export const BacktestResult = () => {
  const {
    activeBacktestId,
    getActiveBacktest,
    setActiveBacktestResultTab,
    runBacktest,
    addStrategy,
    duplicateStrategy,
    deleteStrategy,
    getStrategyDefaults,
  } = useContext(BacktestingContext);

  const activeBacktest = getActiveBacktest();
  const strategyData = activeBacktest?.execution.strategyData;
  const strategyNames = useMemo(
    () => activeBacktest?.execution.strategyNames ?? [],
    [activeBacktest],
  );
  // The overview lists every backtest in the study — always the configured
  // strategy labels, so a freshly duplicated/added variant appears immediately,
  // before it has been run. Run results merge in per-row by name (result name
  // and StrategyTab.label both derive from the same form `name`, see
  // normalizeStrategyData / syncStrategyLabel).
  const displayNames = useMemo(
    () => activeBacktest?.strategies.map((s) => s.label) ?? [],
    [activeBacktest],
  );
  // A backtest is "not run" until its name shows up in the result set. Studies
  // can be mixed — some backtests run, a newly added variant not yet.
  const runNameSet = useMemo(() => new Set(strategyNames), [strategyNames]);
  const notRunNames = useMemo(
    () => new Set(displayNames.filter((name) => !runNameSet.has(name))),
    [displayNames, runNameSet],
  );
  // Labels of variants mid per-variant Run — the overview row shows "Running…"
  // for these. Mapped from the provider's runningStrategyIds via current label.
  const runningNames = useMemo(() => {
    const runningIds = new Set(activeBacktest?.execution.runningStrategyIds ?? []);
    return new Set(
      (activeBacktest?.strategies ?? []).filter((s) => runningIds.has(s.id)).map((s) => s.label),
    );
  }, [activeBacktest]);

  // Single source of truth for which stratlet the detail tabs render — the
  // overview table's row click drives it, same role as opening an account
  // card in Portfolio Performance. Tracked by strategy ID (stable across
  // renames), NOT by label: editing a backtest's name rewrites its label on
  // every keystroke, so a label-keyed selection breaks mid-edit and snaps back
  // to the first row, stealing input focus.
  const strategyIds = useMemo(
    () => activeBacktest?.strategies.map((s) => s.id) ?? [],
    [activeBacktest],
  );
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedStrategyId || !strategyIds.includes(selectedStrategyId)) {
      setSelectedStrategyId(strategyIds[0] ?? null);
    }
  }, [strategyIds, selectedStrategyId]);
  // Follow the provider's active strategy when it changes on a structural
  // mutation (duplicate / add / delete) — so a freshly duplicated variant
  // becomes the selected row. Renames don't touch activeStrategyId, so editing
  // a name keeps the selection (and input focus) put.
  const activeStrategyId = activeBacktest?.activeStrategyId ?? null;
  useEffect(() => {
    if (activeStrategyId) setSelectedStrategyId(activeStrategyId);
  }, [activeStrategyId]);
  // The selected backtest's current label, derived from the ID so it tracks
  // renames. Used for the report lookup, overview highlight and result tabs
  // (reports are keyed by run-time name).
  const selectedStrategy =
    activeBacktest?.strategies.find((s) => s.id === selectedStrategyId)?.label ?? '';
  const selectStrategyByName = (name: string) =>
    setSelectedStrategyId(activeBacktest?.strategies.find((s) => s.label === name)?.id ?? null);

  // One report per stratlet, built once and shared by the overview table and
  // every detail tab below. Empty until the backtest has run.
  const reports = useMemo<Record<string, IPortfolioPerformanceAccountReport | null>>(() => {
    if (!strategyData) return {};
    return Object.fromEntries(
      strategyNames.map((name) => {
        const scenario = strategyData[name];
        return [name, scenario ? buildBacktestReport(scenario, name) : null];
      }),
    );
  }, [strategyData, strategyNames]);

  if (!activeBacktest || !activeBacktestId) return null;

  // A study with no backtests configured (initial load, or deleted the last
  // one) — the shell is the only surface now, so show an empty state here
  // rather than falling through to a landing page.
  if (displayNames.length === 0) return <BacktestEmptyStudy />;

  const activeTab = activeBacktest.execution.activeResultTab;
  const isRunning = activeBacktest.execution.status === 'loading';
  const selectedReport = reports[selectedStrategy] ?? null;
  // Whether the SELECTED backtest has result data. Gating is per-backtest, not
  // per-study: a run study can hold a just-added variant that hasn't run, and
  // selecting it should show the run prompt rather than another backtest's data.
  const selectedHasRun = !!strategyData && runNameSet.has(selectedStrategy);

  // Study-level set-creation surfaced in the Overview table. Duplicate makes a
  // variant of a backtest from its stored config; add drops in a fresh one.
  const handleDuplicate = (name: string) => {
    const strategy = activeBacktest.strategies.find((s) => s.label === name);
    if (!strategy) return;
    duplicateStrategy(
      activeBacktestId,
      strategy.id,
      getStrategyDefaults(activeBacktestId, strategy.id) as StrategyFormData,
    );
  };
  const handleAddBacktest = () => addStrategy(activeBacktestId);
  // Delete is guarded to keep >= 1 backtest per study (also enforced in the
  // provider). The table hides the affordance at the last row.
  const handleDelete = (name: string) => {
    if (displayNames.length <= 1) return;
    const strategy = activeBacktest.strategies.find((s) => s.label === name);
    if (strategy) deleteStrategy(activeBacktestId, strategy.id);
  };

  return (
    <div className="backtest-result">
      <BacktestOverviewTable
        reports={reports}
        strategyNames={displayNames}
        selectedStrategy={selectedStrategy}
        onSelectStrategy={selectStrategyByName}
        notRunNames={notRunNames}
        runningNames={runningNames}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onAddBacktest={handleAddBacktest}
        backtestPeriod={activeBacktest.globalConfig.backtestPeriod}
      />

      <div className="backtest-result-tabs">
        {RESULT_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`backtest-result-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveBacktestResultTab(activeBacktestId, tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="backtest-result-content">
        {/* Specification is the config form — always available, run or not. */}
        {activeTab === 'specification' && <Specification strategyId={selectedStrategyId} />}

        {/* Request is a debug/inspection view — always mountable, gated only on
            whether a request has been sent (handled by its own empty state). */}
        {activeTab === 'request' && (
          <RequestView request={activeBacktest.execution.lastRunRequest} />
        )}

        {/* Result tabs: real views once the selected backtest has run, a run
            prompt before that. */}
        {activeTab !== 'specification' &&
          activeTab !== 'request' &&
          (selectedHasRun && strategyData ? (
            <>
              {activeTab === 'overview' && (
                <Overview
                  report={selectedReport}
                  strategyData={strategyData}
                  selectedStrategy={selectedStrategy}
                />
              )}
              {activeTab === 'market-risk' && (
                <MarketRisk strategyData={strategyData} selectedStrategy={selectedStrategy} />
              )}
              {activeTab === 'market-data' && (
                <MarketData strategyData={strategyData} selectedStrategy={selectedStrategy} />
              )}
              {activeTab === 'transaction-log' && (
                <TransactionLog strategyData={strategyData} selectedStrategy={selectedStrategy} />
              )}
              {activeTab === 'summary' && (
                <Summary strategyData={strategyData} selectedStrategy={selectedStrategy} />
              )}
            </>
          ) : (
            <BacktestRunPrompt onRun={() => void runBacktest()} isRunning={isRunning} />
          ))}
      </div>
    </div>
  );
};
